import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Tooltip } from 'react-tooltip';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import InfoPremiumIcon from 'assets/info_premium_icon.svg?react';
import premium_check_icon from 'assets/premium_check.svg';
import styled from 'styled-components';

import './styles.css';

const FEATURES = [
  'All Images Unblurred',
  'Secret Scenarios: 5x More Content',
  'No Message Limits',
  'Faster Responses',
  'Advanced AI',
  'Customization'
];

interface IIconProps {
  src: string;
}


const OfferDescriptionSection: React.FC = () => {
  const [isExpandedPremiumOfferSection, setIsExpandedPremiumOfferSection] = useState(false);

  const handleClick = () => {
    if (isMobile) {
      setIsExpandedPremiumOfferSection(prev => !prev);
    }
  };

  if (isMobile) {
    return (
      <MobileContainer onClick={handleClick}>
        <MobileHeader>
          <OfferDescriptionText>
            <MobileText>
              Please update your payment details to keep enjoying{' '}
            </MobileText>
            <MobilePremiumText>
              premium features
              <div className="premium-icon">
                <InfoPremiumIcon width={10} height={10} />
              </div>
            </MobilePremiumText>
          </OfferDescriptionText>
          <ChevronIconWrapper $isExpanded={isExpandedPremiumOfferSection}>
            <ChevronDownIcon />
          </ChevronIconWrapper>
        </MobileHeader>

        {isExpandedPremiumOfferSection && (
          <MobileFeaturesList>
            {FEATURES.map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <ItemImage src={premium_check_icon} />
                <MobileText>{feature}</MobileText>
              </div>
            ))}
          </MobileFeaturesList>
        )}
      </MobileContainer>
    );
  }

  return (
    <OfferDescription>
      <div>
        <OfferDescriptionText>
          Please update your payment details to keep<br /> enjoying{' '}
          <PremiumDescription data-tooltip-id="info_chip">
            premium features
            <div className="premium-icon">
              <InfoPremiumIcon width={10.83} height={10.83} />
            </div>
          </PremiumDescription>
        </OfferDescriptionText>
      </div>

      <Tooltip
        id="info_chip"
        place="bottom-end"
        className="offerBannerTooltipContainer"
        opacity={1}
        render={() => (
          <FeaturesWrapper>
            <PremiumTooltipDescription>Premium Features:</PremiumTooltipDescription>
            {FEATURES.map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <ItemImage src={premium_check_icon} />
                <Text>{feature}</Text>
              </div>
            ))}
          </FeaturesWrapper>
        )}
      />
    </OfferDescription>
  );
};

export default OfferDescriptionSection;

const OfferDescription = styled.div`
  color: #A9A9A9;
  position: relative;
  max-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    max-height: unset;
    min-height: auto;
  }
`;


const MobileContainer = styled.div`
  border: 1px solid #282828;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  cursor: pointer;

  @media (max-width: 380px) {
    margin-bottom: 12px;
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const MobileFeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 16px;
`;

const ChevronIconWrapper = styled.div<{ $isExpanded: boolean }>`
  width: 20px;
  height: 20px;
  transform: ${({ $isExpanded }) => $isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease;
  color: #A9A9A9;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const MobileText = styled.span`
  font-family: 'Nunito Sans';
  font-size: 16px;
  line-height: 18px;
  font-weight: 400;
  letter-spacing: 0;
  color: #FFFFFF;

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 14px;
  }
`;

const MobilePremiumText = styled.span`
  font-family: 'Nunito Sans';
  font-size: 14px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0;
  background: linear-gradient(90deg, #9e5eee 0%, #b759ea 32%, #c65de9 66%, #b885f4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  display: inline-flex;
  align-items: center;

  .premium-icon {
    display: inline-flex;
    margin-left: 4px;
    align-items: center;
  }
`;

const OfferDescriptionText = styled.span`
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
  color: #A9A9A9;
`;

const PremiumDescription = styled.span`
  background: linear-gradient(90deg, #9e5eee 0%, #b759ea 32%, #c65de9 66%, #b885f4 100%);
  background-clip: text;
  color: transparent;
  position: relative;

  .premium-icon {
    display: inline-flex;
    margin-left: 4px;
  }
}`;

const PremiumTooltipDescription = styled.div`
  font-family: "Nunito Sans";
  font-size: 18px;
  font-weight: 700;
  line-height: 20px;
  background: linear-gradient(90deg, #9e5eee 0%, #b759ea 32%, #c65de9 66%, #b885f4 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const ItemImage = styled.img<IIconProps>`
  width: 16px;
  height: 16px;
`;

const Text = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: white;
`;

const FeaturesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;